import React, { useState, useEffect, useRef, useCallback } from 'react'
import { LAYER_META } from '../data/injuries'

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const TEAL      = '#0d9488'
const FULL_VB   = '0 0 200 460'
const ANIM_DURATION = 900 // ms for zoom animation

//  ViewBox helpers 
function parseVB(str) {
  const [x, y, w, h] = (str || FULL_VB).split(' ').map(Number)
  return { x, y, w, h }
}
function vbToStr({ x, y, w, h }) {
  return `${x} ${y} ${w} ${h}`
}
function lerpVB(a, b, t) {
  return { x: a.x+(b.x-a.x)*t, y: a.y+(b.y-a.y)*t, w: a.w+(b.w-a.w)*t, h: a.h+(b.h-a.h)*t }
}
function easeInOut(t) {
  return t < 0.5 ? 2*t*t : -1+(4-2*t)*t
}

//  Region definitions 
const FRONT_REGIONS = new Set([
  'Head / Neck','Left Shoulder','Right Shoulder','Chest / Ribs',
  'Abdomen / Groin','Left Elbow','Right Elbow','Left Wrist / Hand','Right Wrist / Hand',
  'Left Hip','Right Hip','Left Knee','Right Knee','Left Ankle / Foot','Right Ankle / Foot',
])
const BACK_REGIONS = new Set([
  'Head / Neck','Upper Back','Mid Back','Lower Back',
  'Left Shoulder','Right Shoulder','Left Elbow','Right Elbow',
  'Left Wrist / Hand','Right Wrist / Hand',
  'Left Hip','Right Hip','Left Knee','Right Knee','Left Ankle / Foot','Right Ankle / Foot',
])

const SHAPES = {
  front: {
    'Head / Neck':       [{ type:'ellipse', cx:100, cy:38,  rx:28,  ry:32 }, { type:'rect', x:88, y:68, w:24, h:18, r:8 }],
    'Left Shoulder':     [{ type:'ellipse', cx:44,  cy:103, rx:22,  ry:20 }],
    'Right Shoulder':    [{ type:'ellipse', cx:156, cy:103, rx:22,  ry:20 }],
    'Chest / Ribs':      [{ type:'rect',    x:62,   y:86,   w:76,   h:68, r:14 }],
    'Abdomen / Groin':   [{ type:'rect',    x:65,   y:152,  w:70,   h:62, r:12 }],
    'Left Elbow':        [{ type:'ellipse', cx:30,  cy:180, rx:14,  ry:13 }],
    'Right Elbow':       [{ type:'ellipse', cx:170, cy:180, rx:14,  ry:13 }],
    'Left Wrist / Hand': [{ type:'rect',    x:18,   y:228,  w:24,   h:44, r:9  }],
    'Right Wrist / Hand':[{ type:'rect',    x:158,  y:228,  w:24,   h:44, r:9  }],
    'Left Hip':          [{ type:'ellipse', cx:74,  cy:228, rx:20,  ry:18 }],
    'Right Hip':         [{ type:'ellipse', cx:126, cy:228, rx:20,  ry:18 }],
    'Left Knee':         [{ type:'ellipse', cx:74,  cy:333, rx:18,  ry:16 }],
    'Right Knee':        [{ type:'ellipse', cx:126, cy:333, rx:18,  ry:16 }],
    'Left Ankle / Foot': [{ type:'rect',    x:59,   y:410,  w:34,   h:22, r:8  }, { type:'ellipse', cx:69,  cy:408, rx:12, ry:8 }],
    'Right Ankle / Foot':[{ type:'rect',    x:107,  y:410,  w:34,   h:22, r:8  }, { type:'ellipse', cx:131, cy:408, rx:12, ry:8 }],
  },
  back: {
    'Head / Neck':       [{ type:'ellipse', cx:100, cy:38,  rx:28,  ry:32 }, { type:'rect', x:88, y:68, w:24, h:18, r:8 }],
    'Upper Back':        [{ type:'rect',    x:62,   y:86,   w:76,   h:52, r:12 }],
    'Mid Back':          [{ type:'rect',    x:64,   y:136,  w:72,   h:42, r:10 }],
    'Lower Back':        [{ type:'rect',    x:65,   y:176,  w:70,   h:38, r:10 }],
    'Left Shoulder':     [{ type:'ellipse', cx:44,  cy:103, rx:22,  ry:20 }],
    'Right Shoulder':    [{ type:'ellipse', cx:156, cy:103, rx:22,  ry:20 }],
    'Left Elbow':        [{ type:'ellipse', cx:30,  cy:180, rx:14,  ry:13 }],
    'Right Elbow':       [{ type:'ellipse', cx:170, cy:180, rx:14,  ry:13 }],
    'Left Wrist / Hand': [{ type:'rect',    x:18,   y:228,  w:24,   h:44, r:9  }],
    'Right Wrist / Hand':[{ type:'rect',    x:158,  y:228,  w:24,   h:44, r:9  }],
    'Left Hip':          [{ type:'ellipse', cx:74,  cy:228, rx:20,  ry:18 }],
    'Right Hip':         [{ type:'ellipse', cx:126, cy:228, rx:20,  ry:18 }],
    'Left Knee':         [{ type:'ellipse', cx:74,  cy:333, rx:18,  ry:16 }],
    'Right Knee':        [{ type:'ellipse', cx:126, cy:333, rx:18,  ry:16 }],
    'Left Ankle / Foot': [{ type:'rect',    x:59,   y:410,  w:34,   h:22, r:8  }],
    'Right Ankle / Foot':[{ type:'rect',    x:107,  y:410,  w:34,   h:22, r:9  }],
  },
}

//  Shape renderer 
function Shape({ shape, fill, stroke, strokeWidth, onClick, style, onMouseEnter, onMouseLeave }) {
  const props = { fill, stroke, strokeWidth, onClick, style, onMouseEnter, onMouseLeave, strokeLinejoin:'round' }
  if (shape.type === 'ellipse') return <ellipse cx={shape.cx} cy={shape.cy} rx={shape.rx} ry={shape.ry} {...props} />
  if (shape.type === 'rect')    return <rect x={shape.x} y={shape.y} width={shape.w} height={shape.h} rx={shape.r||0} {...props} />
  return null
}

//  Main BodyMap component 
export default function BodyMap({
  // Legacy props (intake form mode)
  highlightedRegions = [],
  onRegionClick,
  readOnly = false,
  // Enhanced props (InjuryExplorer mode)
  targetViewBox = null,       // string "x y w h" — triggers animated zoom
  highlightLayer = null,      // layer type string (from LAYER_META)
  forceView = null,           // 'front' | 'back' — override internal toggle
  showLayerBadge = false,     // show layer type badge on map
  compact = false,            // smaller display for dashboards
  onZoomReset = null,         // callback when user clicks reset zoom
}) {
  const [view, setView]               = useState('front')
  const [hovered, setHovered]         = useState(null)
  const [currentVB, setCurrentVB]     = useState(FULL_VB)
  const [isZoomed, setIsZoomed]       = useState(false)
  const [pulseActive, setPulseActive] = useState(false)

  const animRef     = useRef(null)
  const startVBRef  = useRef(parseVB(FULL_VB))
  const targetVBRef = useRef(parseVB(FULL_VB))
  const startTimeRef= useRef(null)

  // Sync forced view
  useEffect(() => { if (forceView) setView(forceView) }, [forceView])

  //  Animated zoom 
  const animateToVB = useCallback((targetStr) => {
    if (animRef.current) cancelAnimationFrame(animRef.current)
    startVBRef.current  = parseVB(currentVB)
    targetVBRef.current = parseVB(targetStr)
    startTimeRef.current = null

    const tick = (ts) => {
      if (!startTimeRef.current) startTimeRef.current = ts
      const elapsed = ts - startTimeRef.current
      const t = Math.min(elapsed / ANIM_DURATION, 1)
      const eased = easeInOut(t)
      const vb = lerpVB(startVBRef.current, targetVBRef.current, eased)
      setCurrentVB(vbToStr(vb))
      if (t < 1) {
        animRef.current = requestAnimationFrame(tick)
      } else {
        animRef.current = null
        // Trigger pulse after zoom lands
        setPulseActive(false)
        setTimeout(() => setPulseActive(true), 100)
        setTimeout(() => setPulseActive(false), 2500)
      }
    }
    animRef.current = requestAnimationFrame(tick)
  }, [currentVB])

  // Respond to targetViewBox prop changes
  useEffect(() => {
    if (targetViewBox && targetViewBox !== FULL_VB) {
      animateToVB(targetViewBox)
      setIsZoomed(true)
    } else if (!targetViewBox) {
      animateToVB(FULL_VB)
      setIsZoomed(false)
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetViewBox])

  const handleResetZoom = () => {
    animateToVB(FULL_VB)
    setIsZoomed(false)
    setPulseActive(false)
    onZoomReset?.()
  }

  //  Highlight colors 
  const highlighted = new Set(highlightedRegions)

  const getHighlightColor = (region) => {
    if (!highlighted.has(region)) return null
    // Use layer color if provided, else heatmap by region type
    if (highlightLayer && LAYER_META[highlightLayer]) {
      return LAYER_META[highlightLayer].color
    }
    const lbl = region.toLowerCase()
    if (lbl.includes('back') || lbl.includes('neck') || lbl.includes('head')) return '#dc2626'
    if (lbl.includes('shoulder') || lbl.includes('elbow') || lbl.includes('wrist')) return '#ea580c'
    if (lbl.includes('knee') || lbl.includes('ankle') || lbl.includes('hip')) return '#d97706'
    return '#ef4444'
  }

  const layerMeta = highlightLayer ? LAYER_META[highlightLayer] : null

  const shapes = SHAPES[view]
  const visibleRegions = view === 'front' ? FRONT_REGIONS : BACK_REGIONS

  const maxW = compact ? 160 : 220

  return (
    <div style={{ fontFamily: FONT, userSelect: 'none' }}>
      {/* Controls row */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px', gap:'6px' }}>
        {/* Front / Back toggle */}
        {!forceView && (
          <div style={{ display:'flex', gap:'4px' }}>
            {['front','back'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: compact ? '3px 11px' : '4px 14px',
                borderRadius:'99px', fontSize:'11px', fontWeight:'700',
                cursor:'pointer', fontFamily: FONT, textTransform:'capitalize',
                background: view === v ? TEAL : 'white',
                color:      view === v ? 'white' : '#64748b',
                border:     `1.5px solid ${view === v ? TEAL : '#e2e8f0'}`,
                transition: 'all 0.15s',
              }}>{v}</button>
            ))}
          </div>
        )}
        {forceView && <div style={{ fontSize:'11px', color:'#94a3b8', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.06em' }}>{forceView} view</div>}

        {/* Layer badge */}
        {showLayerBadge && layerMeta && (
          <div style={{
            fontSize:'10px', fontWeight:'700', padding:'3px 9px', borderRadius:'99px',
            background: layerMeta.light, color: layerMeta.color,
            border: `1px solid ${layerMeta.border}`,
            textTransform:'uppercase', letterSpacing:'0.06em',
          }}>
             {layerMeta.label}
          </div>
        )}

        {/* Reset zoom button */}
        {isZoomed && (
          <button onClick={handleResetZoom} style={{
            padding:'3px 10px', fontSize:'10px', fontWeight:'600', cursor:'pointer',
            border:`1px solid #e2e8f0`, borderRadius:'99px', background:'white',
            color:'#64748b', fontFamily:FONT, transition:'all 0.12s',
          }}>⊖ Zoom Out</button>
        )}
      </div>

      {/* SVG */}
      <svg
        viewBox={currentVB}
        width="100%"
        style={{ maxWidth: `${maxW}px`, display:'block', margin:'0 auto', transition: 'none' }}
      >
        {/*  Body silhouette  */}
        {/* Head */}
        <ellipse cx={100} cy={38}  rx={30} ry={34}  fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        <rect    x={88}  y={69}   width={24} height={19} rx={8}   fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        {/* Torso */}
        <rect x={62} y={86}  width={76} height={128} rx={18} fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        {/* Upper arms */}
        <rect x={22}  y={96}  width={24} height={90} rx={11} fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        <rect x={154} y={96}  width={24} height={90} rx={11} fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        {/* Forearms */}
        <rect x={20}  y={185} width={20} height={88} rx={9}  fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        <rect x={160} y={185} width={20} height={88} rx={9}  fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        {/* Upper legs */}
        <rect x={62}  y={245} width={34} height={100} rx={14} fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        <rect x={104} y={245} width={34} height={100} rx={14} fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        {/* Lower legs */}
        <rect x={63}  y={343} width={32} height={70} rx={12} fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        <rect x={105} y={343} width={32} height={70} rx={12} fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        {/* Feet */}
        <rect x={57}  y={410} width={42} height={22} rx={9}  fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />
        <rect x={101} y={410} width={42} height={22} rx={9}  fill="#fde8d0" stroke="#e8c8a8" strokeWidth="1.2" />

        {/* Back view anatomy lines */}
        {view === 'back' && (
          <>
            <line x1={100} y1={88} x2={100} y2={213} stroke="#e8c8a8" strokeWidth="1" strokeDasharray="3,3" />
            <line x1={65}  y1={137} x2={135} y2={137} stroke="#e8c8a8" strokeWidth="0.8" />
            <line x1={65}  y1={177} x2={135} y2={177} stroke="#e8c8a8" strokeWidth="0.8" />
          </>
        )}

        {/*  Region overlays  */}
        {Object.entries(shapes).map(([region, shapeList]) => {
          if (!visibleRegions.has(region)) return null
          const hlColor = getHighlightColor(region)
          const isHighlighted = !!hlColor
          const isHovered = hovered === region && !readOnly

          return shapeList.map((shape, i) => {
            const showFill = isHighlighted || isHovered
            const fillColor = isHighlighted
              ? (pulseActive ? hlColor : hlColor + 'cc')
              : isHovered
              ? 'rgba(13,148,136,0.18)'
              : 'transparent'
            const strokeColor = isHighlighted ? hlColor : isHovered ? TEAL : 'transparent'

            return (
              <Shape
                key={region + i}
                shape={shape}
                fill={showFill ? fillColor : 'transparent'}
                stroke={showFill ? strokeColor : 'transparent'}
                strokeWidth={isHighlighted ? 2 : 1}
                onClick={!readOnly ? () => onRegionClick?.(region) : undefined}
                onMouseEnter={!readOnly ? () => setHovered(region) : undefined}
                onMouseLeave={!readOnly ? () => setHovered(null)   : undefined}
                style={{
                  cursor: readOnly ? 'default' : 'pointer',
                  transition: 'fill 0.3s, stroke 0.3s',
                  filter: isHighlighted && pulseActive ? `drop-shadow(0 0 6px ${hlColor}aa)` : 'none',
                }}
              />
            )
          })
        })}

        {/* Pulse rings on highlighted regions */}
        {pulseActive && Object.entries(shapes).map(([region, shapeList]) => {
          if (!visibleRegions.has(region) || !highlighted.has(region)) return null
          const hlColor = getHighlightColor(region)
          return shapeList.slice(0,1).map((shape, i) => {
            if (shape.type === 'ellipse') return (
              <ellipse key={'pulse-'+region+i}
                cx={shape.cx} cy={shape.cy} rx={shape.rx+6} ry={shape.ry+6}
                fill="none" stroke={hlColor} strokeWidth="2" opacity="0.4"
                style={{ animation: 'bodyPulse 1.2s ease-out infinite' }}
              />
            )
            if (shape.type === 'rect') return (
              <rect key={'pulse-'+region+i}
                x={shape.x-5} y={shape.y-5} width={shape.w+10} height={shape.h+10} rx={(shape.r||0)+5}
                fill="none" stroke={hlColor} strokeWidth="2" opacity="0.4"
                style={{ animation: 'bodyPulse 1.2s ease-out infinite' }}
              />
            )
            return null
          })
        })}

        {/* L / R labels */}
        <text x={14}  y={310} fontSize="7" fill="#d1d5db" fontFamily="Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif" textAnchor="middle" transform="rotate(-90,14,310)">LEFT</text>
        <text x={186} y={310} fontSize="7" fill="#d1d5db" fontFamily="Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif" textAnchor="middle" transform="rotate(90,186,310)">RIGHT</text>
      </svg>

      {/* Highlighted region chips */}
      {highlighted.size > 0 && (
        <div style={{ marginTop:'10px', display:'flex', flexWrap:'wrap', gap:'4px', justifyContent:'center' }}>
          {[...highlighted].map(r => {
            const color = getHighlightColor(r) || '#dc2626'
            return (
              <span key={r} style={{
                fontSize:'10px', fontWeight:'700', padding:'2px 8px', borderRadius:'99px',
                background: color + '18', color, border: `1px solid ${color}44`,
              }}>{r}</span>
            )
          })}
        </div>
      )}

      {/* Hover tooltip */}
      {hovered && !readOnly && (
        <div style={{ textAlign:'center', marginTop:'6px', fontSize:'11px', color:TEAL, fontWeight:'600' }}>
          {hovered}
        </div>
      )}

      <style>{`
        @keyframes bodyPulse {
          0%   { opacity: 0.5; transform: scale(1);    }
          50%  { opacity: 0.2; transform: scale(1.08); }
          100% { opacity: 0;   transform: scale(1.16); }
        }
      `}</style>
    </div>
  )
}
